// The chat log of the dock (ui-map-interface.md §12.0 "The chat"): the
// lines of the open channel as markup, and the newest line the chat line
// names (R1799). The CLAN tab reads the meta server's room (R225); WORLD,
// FACTION and SQUAD read the game socket. A line of another player carries
// the ⋯ of REPORT and BLOCK of the report desk (R1323, R1328).
//
// Each function reads the state and the few readings the overlay hands it.
// The overlay writes the log under its change guard.

import { type ChatMsg, ChatChannel } from '../../gen/proto/aquaphobia';
import { overlay as S, quickchat as QS } from '../../gen/strings';
import type { GameState } from '../../state';
import type { ChatFresh } from '../chatdock';
import { chatStampHtml } from '../chatstamp';
import { chatText } from '../chatfilter';
import { cssColor } from '../format';
import { esc } from '../esc';
import { posText, quickText, type PosLookup } from '../quickchat';
import { relayMarkLine } from '../relaymark';
import type { ReportDesk } from './report';

/** What the log reads beside the state. */
export interface ChatLogCtx {
  /** The report desk: the ⋯ of a line and the blocks the tabs hide. */
  desk: ReportDesk;
  /** The seat's own account id: an own line carries no ⋯. */
  selfId: number;
  /** The chat time stamp is on (R1791). */
  stamps: boolean;
  /** The chat filter is on (R2937): a listed word reads as `•`. */
  filter: boolean;
  /** What a quick line's position reads (R1607). */
  posLook: PosLookup;
}

/** The line under the log after a BLOCK: the confirmation or the refusal. */
function blockLineHtml(desk: ReportDesk): string {
  return desk.blockLine === '' ? '' : `<div class="line muted">${esc(desk.blockLine)}</div>`;
}

/**
 * The CLAN tab (R225). The room is on the meta server and a second
 * socket carries it, so this branch reads `state.clanChat` and never
 * `state.chat`: the game socket refuses every clan line.
 *
 * Three stop reasons and one retry state are all the player can act on,
 * so each one gets a plain muted line instead of an empty box.
 */
export function clanChatHtml(s: GameState, ctx: ChatLogCtx): string {
  const c = s.clanChat;
  if (c.stop === 'no-clan') {
    return `<div class="line muted">${S.chat_no_clan}</div>`;
  }
  if (c.stop !== null) {
    return `<div class="line muted">${S.chat_meta_down}</div>`;
  }
  const desk = ctx.desk;
  // The chat relay of the clan copies this tab to Discord (R1641): the
  // → DISCORD line stands at the top of the log while it is on.
  let html = (c.relay ? relayMarkLine() : '') + c.entries.map((e) => {
    if (e.kind === 'gap') {
      return `<div class="line muted">${S.chat_missed({ n: e.missed })}</div>`;
    }
    if (desk.blockedIds.has(e.senderId)) return '';
    // Own lines are marked, because the transcript carries no faction
    // colour: the meta server hosts no world and knows no faction.
    const own = e.senderId !== '' && e.senderId === c.selfId;
    // The chat time stamp (R1791): a replayed line keeps the time the
    // meta server stored it at.
    const who = chatStampHtml(e.at, ctx.stamps)
      + `<span class="who${own ? ' own' : ''}">${esc(e.sender)}</span>`;
    // Another member's line carries the ⋯ of REPORT and BLOCK
    // (deployment.md §7.6, R1323), as the hub's CLAN page does. A line
    // of a deleted account carries no public id and no control.
    const body = esc(chatText(e, e.body, ctx.filter));
    if (own || e.senderId === '') return `<div class="line">${who} ${body}</div>`;
    const key = `clan:${e.seq}`;
    const more = desk.lineMore(key, {
      head: S.report_head_line({ name: esc(e.sender) }), clanSeq: e.seq,
    });
    return `<div class="line">${who} ${body} ${more}</div>`
      + desk.lineMenuRow(key, `data-act="lineblock" data-key="${esc(e.senderId)}"`);
  }).join('');
  html += blockLineHtml(desk);
  if (!c.connected) {
    html += `<div class="line muted">${S.chat_reconnecting}</div>`;
  }
  return html;
}

/** The three channels the game socket carries: WORLD, FACTION and SQUAD. */
export function worldChatHtml(s: GameState, ctx: ChatLogCtx): string {
  const desk = ctx.desk;
  const lines = s.chat.filter((l) => l.channel === s.channel && !desk.hidesLine(l)).slice(-60);
  const html = lines.map((l) => {
    // The chat time stamp (R1791) stands first, before the sender and
    // before the ◆ of a quick line.
    const ts = chatStampHtml(l.unix, ctx.stamps);
    const name = l.fromId === 0
      ? `<span class="sys">${esc(l.fromName)}</span>`
      : `<span class="who" style="color:${cssColor(l.faction)}">`
        + `${l.clanTag ? `[${esc(l.clanTag)}] ` : ''}${esc(l.fromName)}</span>`;
    const who = ts + name;
    // A quick line (R1607, card 04): the ◆ before the name, the line in
    // the reader's own language and the bright ink, and the position as
    // a ⌖ pill that moves the camera there. It is no content to report,
    // so it carries no ⋯ (R1609); a block hides it like any line.
    if (l.quick > 0) return quickLineHtml(l, ts, name, ctx);
    // A probe scan line reads `REPORT SHOW 5 contacts` (R203). The two
    // buttons come first, because the count is the sentence and a control
    // parked after it reads as an afterthought. The scout's name is not in
    // the text: the line already names its sender, on the left.
    //
    // SHOW is drawn only when the report carries a cell. An older server
    // sends none, and a jump to cell 0 is a jump to the corner of the map.
    const cell = l.report?.cell ?? 0;
    // A map marker line carries SHOW alone (player-reach.md §2.1, R1292).
    // The line holds a copy of the marker as it was placed, so the jump
    // works after the marker has gone. Every cell is a real cell here,
    // cell 0 as well, because the copy itself says a marker was placed.
    const acts = l.report
      ? `<button class="report" data-act="report" data-n="${s.chatDropped + s.chat.indexOf(l)}">${S.chat_report}</button>`
        + (cell ? ` <button class="report" data-act="focuscell" data-n="${cell}">${S.chat_show}</button>` : '')
        + ' '
      : l.marker
        ? `<button class="report" data-act="focuscell" data-n="${l.marker.cell}">${S.chat_show}</button> `
        : '';
    // A line another player wrote carries the ⋯ of REPORT (R1323). The
    // world's own voice, an own line, a scan line and a marker line
    // carry the server's words or the player's own, and a line of an
    // older server carries no line id.
    const other = l.fromId !== 0 && l.fromId !== ctx.selfId && l.lineId > 0
      && !l.report && !l.marker && !l.quick;
    const text = esc(chatText(l, l.text, ctx.filter));
    if (!other) return `<div class="line">${who} ${acts}${text}</div>`;
    const key = `line:${l.lineId}`;
    const more = desk.lineMore(key, {
      head: S.report_head_line({ name: esc(l.fromName) }),
      game: { kind: 'line', lineId: l.lineId },
    });
    return `<div class="line">${who} ${acts}${text} ${more}</div>`
      + desk.lineMenuRow(key, `data-act="lineblockseat" data-n="${l.fromId}"`);
  }).join('');
  return html + blockLineHtml(desk);
}

/** One quick line of the log (R1607, mockup round 077, card 04). */
function quickLineHtml(l: ChatMsg, ts: string, who: string, ctx: ChatLogCtx): string {
  const own = l.fromId !== 0 && l.fromId === ctx.selfId;
  const pos = l.quickPos;
  const pill = pos
    ? `<button class="qpos" data-act="focuscell" data-n="${pos.cell}" title="${esc(QS.pos_title)}">`
      + `${esc(posText(pos, ctx.posLook))}</button>`
    : '';
  return `<div class="line q${own ? ' own' : ''}">${ts}<span class="qm">◆</span>${who} `
    + `<span class="qt">${esc(quickText(l.quick))}</span>${pill}</div>`;
}

/**
 * The newest live line as the chat line names it (R1799), or null: no
 * line, an own line, or a line of a sender this seat blocked. `filter`
 * masks the listed words (R2937).
 */
export function chatFresh(s: GameState, desk: ReportDesk, selfId: number, filter = false): ChatFresh | null {
  const f = s.chatFresh;
  if (!f) return null;
  if (f.kind === 'clan') {
    const e = f.entry;
    if (desk.blockedIds.has(e.senderId) || e.senderId === s.clanChat.selfId) return null;
    return {
      channel: ChatChannel.CHAT_CHANNEL_CLAN, sender: e.sender, text: chatText(e, e.body, filter),
      faction: 0, quick: false, atMs: f.atMs,
    };
  }
  const l = f.line;
  if (l.fromId !== 0 && l.fromId === selfId) return null;
  if (desk.hidesLine(l)) return null;
  const tag = l.clanTag ? `[${l.clanTag}] ` : '';
  return {
    channel: l.channel,
    sender: l.fromId === 0 ? l.fromName : tag + l.fromName,
    text: l.quick > 0 ? quickText(l.quick) : chatText(l, l.text, filter),
    faction: l.fromId === 0 ? 0 : l.faction,
    quick: l.quick > 0,
    atMs: f.atMs,
  };
}
